const BUSTLY_HOSTED_CONNECT_PROVIDER_KEYS: Record<string, string> = {
    'google-analytics': 'google-analytics',
    'google-search-console': 'google-search-console',
    semrush: 'semrush',
    woocommerce: 'woocommerce',
    magento: 'adobe-commerce',
    'adobe-commerce': 'adobe-commerce',
    'google-ads': 'google-ads',
    'tiktok-ads': 'tiktok-ads',
    bigcommerce: 'bigcommerce',
    klaviyo: 'klaviyo-oauth',
    'klaviyo-oauth': 'klaviyo-oauth',
    mailchimp: 'mailchimp',
    'mailchimp-oauth': 'mailchimp',
};

const normalizeHostedConnectProvider = (provider?: string | null) => String(provider || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

export const resolveBustlyHostedConnectIntegrationKey = (
    provider?: string | null
): string | null => (
    BUSTLY_HOSTED_CONNECT_PROVIDER_KEYS[normalizeHostedConnectProvider(provider)] || null
);

export const isBustlyHostedConnectProvider = (provider?: string | null): boolean => (
    resolveBustlyHostedConnectIntegrationKey(provider) !== null
);

export const requiresBustlyHostedAccountSelection = (provider?: string | null): boolean => (
    normalizeHostedConnectProvider(provider) === 'google-ads'
);

export type IntegrationConnectEntryPoint =
    | {
          kind: 'hosted-connect';
          integrationKey: string;
          requiresAccountSelection: boolean;
      }
    | { kind: 'legacy-store-url' }
    | { kind: 'legacy-credential' }
    | { kind: 'direct-connect' };

export const resolveIntegrationConnectEntryPoint = (
    provider: string,
    options: {
        hasLegacyStoreUrlInput?: boolean;
        hasLegacyCredentialInput?: boolean;
    } = {},
): IntegrationConnectEntryPoint => {
    const integrationKey = resolveBustlyHostedConnectIntegrationKey(provider);
    if (integrationKey) {
        return {
            kind: 'hosted-connect',
            integrationKey,
            requiresAccountSelection: requiresBustlyHostedAccountSelection(provider),
        };
    }
    if (options.hasLegacyStoreUrlInput) {
        return { kind: 'legacy-store-url' };
    }
    if (options.hasLegacyCredentialInput) {
        return { kind: 'legacy-credential' };
    }
    return { kind: 'direct-connect' };
};

export type BustlyHostedConnectDispatchResult<T> =
    | { handled: false }
    | { handled: true; result: T };

/**
 * Executes the Hosted Connect branch selected by the shared routing contract.
 * Settings and Onboarding both eventually call the workspace connect function,
 * so keeping this final dispatch executable and testable prevents a provider
 * from silently falling back to its legacy Nango implementation.
 */
export const dispatchBustlyHostedConnect = async <T>(
    provider: string,
    connectHosted: (
        integrationKey: string,
        requiresAccountSelection: boolean,
    ) => Promise<T>,
): Promise<BustlyHostedConnectDispatchResult<T>> => {
    const entryPoint = resolveIntegrationConnectEntryPoint(provider);
    if (entryPoint.kind !== 'hosted-connect') {
        return { handled: false };
    }
    return {
        handled: true,
        result: await connectHosted(
            entryPoint.integrationKey,
            entryPoint.requiresAccountSelection,
        ),
    };
};
